import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import { v4 as uuidv4 } from 'uuid';
import UserCombobox from '../../../util_components/user_combobox/UserCombobox';
import CustomModal from '../../../util_components/custom_modal';
import {
  ALREADY_ASSIGNED,
  TARGET_INELIGIBLE,
  apiCurrentAdvisorName,
  apiErrorCode,
  apiErrorReason,
  assignAdvisor,
  fetchAdvisors
} from './api';
import {
  EMPTY_FILTERS,
  alreadyAssignedNotice,
  assignConfirmText,
  assignNotice,
  assignRequest,
  eligibleTargets,
  listQuery
} from './advisors';
import { SHARED_STYLES } from '../styles';
import type { AdvisorSummary } from './types';

export type Notify = (type: 'success' | 'error', message: string) => void;

export interface AssignStudent {
  uuid: string;
  name: string;
}

interface Props {
  student: AssignStudent | null;
  notify: Notify;
  onClose: () => void;
  // Called after the server answered (assigned, duplicate or 409) so the host refreshes.
  onDone: () => void;
}

const { MUTED, PRIMARY_BTN, SECONDARY_BTN } = SHARED_STYLES;

// goserver 400 for a banned / deleted student (reason: banned|deleted).
const STUDENT_INELIGIBLE = 'STUDENT_INELIGIBLE';
const STUDENT_INELIGIBLE_MESSAGE = 'This student is banned/deleted and cannot be assigned';

// Mounted only while open, so the request_ref is minted fresh per open and kept across retries.
function AssignBody({
  student,
  notify,
  onClose,
  onDone,
  busyRef
}: Props & { student: AssignStudent; busyRef: MutableRefObject<boolean> }) {
  const [requestRef] = useState(() => uuidv4());
  const [summary, setSummary] = useState<AdvisorSummary[]>([]);
  const [loadError, setLoadError] = useState('');
  const [target, setTarget] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const targets = useMemo(() => eligibleTargets(summary), [summary]);
  const targetName = targets.find((t) => t.uuid === target)?.name || '';
  // Late callbacks must not close or refresh a dialog opened for someone else.
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
      busyRef.current = false;
    },
    [busyRef]
  );

  // limit=1: only the advisor summary is needed, not the rows.
  useEffect(() => {
    let live = true;
    fetchAdvisors(listQuery(EMPTY_FILTERS, 0, 1))
      .then((d) => live && setSummary(d.summary))
      .catch((e: unknown) => live && setLoadError(apiErrorReason(e, 'Failed to load advisors')));
    return () => {
      live = false;
    };
  }, []);

  const onConfirm = () => {
    if (!target || busy) return;
    setBusy(true);
    busyRef.current = true;
    assignAdvisor(assignRequest(student.uuid, target, requestRef))
      .then((res) => {
        notify('success', assignNotice(student.name, res));
        if (!alive.current) return;
        onClose();
        onDone();
      })
      .catch((e: unknown) => {
        const code = apiErrorCode(e);
        if (code === ALREADY_ASSIGNED) {
          notify('error', alreadyAssignedNotice(apiCurrentAdvisorName(e)));
          if (!alive.current) return;
          onClose();
          onDone();
          return;
        }
        const reason = apiErrorReason(e, 'Failed to assign the advisor');
        if (code === STUDENT_INELIGIBLE) notify('error', STUDENT_INELIGIBLE_MESSAGE);
        else
          notify(
            'error',
            code === TARGET_INELIGIBLE ? `Target advisor is not eligible: ${reason}` : reason
          );
        if (alive.current) setConfirming(false);
      })
      .finally(() => {
        if (!alive.current) return;
        busyRef.current = false;
        setBusy(false);
      });
  };

  if (confirming) {
    return (
      <div role="alertdialog" aria-label="Confirm assign advisor">
        <p className="text-sm">{assignConfirmText(student.name, targetName)}</p>
        <p className="mt-2 text-xs" style={MUTED}>
          If this student&apos;s latest trial was recorded as a no-show less than 2h15m ago, the
          assignment will auto-end.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setConfirming(false)}
            disabled={busy}
            className="rounded-md px-4 py-1.5 text-sm font-medium"
            style={SECONDARY_BTN}
          >
            Back
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="rounded-md px-4 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
            style={PRIMARY_BTN}
          >
            {busy ? 'Assigning…' : 'Yes, assign advisor'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div role="dialog" aria-label="Assign advisor">
      <p className="text-sm" style={MUTED}>{`Pick the advisor for ${student.name}.`}</p>
      <div className="mt-3">
        <UserCombobox
          id="assign-advisor-target"
          label="Advisor"
          placeholder="Assign to…"
          users={targets}
          value={target}
          on_change={setTarget}
          width={280}
        />
      </div>
      {loadError ? (
        <p role="alert" className="mt-2 text-xs" style={{ color: 'var(--state-error)' }}>
          {loadError}
        </p>
      ) : null}
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md px-4 py-1.5 text-sm font-medium"
          style={SECONDARY_BTN}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => setConfirming(true)}
          disabled={!target}
          className="rounded-md px-4 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
          style={PRIMARY_BTN}
        >
          Continue
        </button>
      </div>
    </div>
  );
}

// "Assign advisor" for a student with no active advisor (REQ 57); shared by the Not-assigned
// view and the student profile.
function AssignAdvisorModal({ student, notify, onClose, onDone }: Props) {
  const busyRef = useRef(false);
  // Escape, overlay and X all land here: closing mid-request would orphan the response.
  const guardedClose = () => {
    if (!busyRef.current) onClose();
  };
  return (
    <CustomModal
      show={!!student}
      close={guardedClose}
      title="Assign advisor"
      inner_style={{ width: 'min(480px, 96vw)', borderRadius: 12 }}
    >
      {student ? (
        <AssignBody
          key={student.uuid}
          student={student}
          notify={notify}
          onClose={onClose}
          onDone={onDone}
          busyRef={busyRef}
        />
      ) : null}
    </CustomModal>
  );
}

export default AssignAdvisorModal;
